import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api_config';

const EmitResearchOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [profile, setProfile] = useState({
    academic_level: '',
    institution: '',
    field_of_study: '',
    preferred_citation_style: '',
    writing_goals: [],
    additional_context: ''
  });

  // Load existing profile if editing, or stay for new setup
  const [isEditing, setIsEditing] = useState(false);
  
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await API.get('/api/v1/emit-research/profile');
        if (res.data.has_profile) {
          // Load existing data for editing
          setIsEditing(true);
          setProfile({
            academic_level: res.data.profile.academic_level || '',
            institution: res.data.profile.institution || '',
            field_of_study: res.data.profile.field_of_study || '',
            preferred_citation_style: res.data.profile.preferred_citation_style || '',
            writing_goals: res.data.profile.writing_goals || [],
            additional_context: res.data.profile.additional_context || ''
          });
        }
      } catch (err) {
        // No profile, stay on setup for new profile
      }
    };
    loadProfile();
  }, []);

  const academicLevels = [
    { value: 'undergraduate', label: 'Undergraduate', emoji: '📚', desc: 'Bachelor\'s degree program' },
    { value: 'graduate', label: 'Graduate', emoji: '🎓', desc: 'Master\'s degree program' },
    { value: 'doctoral', label: 'Doctoral', emoji: '🎯', desc: 'PhD or doctoral program' },
    { value: 'professional', label: 'Professional', emoji: '💼', desc: 'Law, Medical, MBA, etc.' },
    { value: 'continuing', label: 'Continuing Education', emoji: '📖', desc: 'Certificates, professional development' }
  ];

  const citationStyles = [
    { value: 'turabian', label: 'Turabian/Chicago', desc: 'Footnotes, common in humanities & theology' },
    { value: 'apa', label: 'APA 7th Edition', desc: 'Author-date, common in social sciences' },
    { value: 'mla', label: 'MLA 9th Edition', desc: 'Author-page, common in literature & arts' },
    { value: 'chicago', label: 'Chicago (Author-Date)', desc: 'Alternative Chicago style' }
  ];

  const writingGoals = [
    'Pass plagiarism detection (Turnitin, SafeAssign)',
    'Improve academic writing skills',
    'Learn proper citation formatting',
    'Complete thesis or dissertation',
    'Write research papers for publication',
    'Professional development writing',
    'Course assignments and essays'
  ];

  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');
    try {
      await API.post('/api/v1/emit-research/profile', profile);
      navigate('/emit-research');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleGoal = (goal) => {
    setProfile(prev => ({
      ...prev,
      writing_goals: prev.writing_goals.includes(goal)
        ? prev.writing_goals.filter(g => g !== goal)
        : [...prev.writing_goals, goal]
    }));
  };

  const canProceed = () => {
    switch(step) {
      case 1: return !!profile.academic_level;
      case 2: return !!profile.institution && !!profile.field_of_study;
      case 3: return !!profile.preferred_citation_style;
      case 4: return profile.writing_goals.length > 0;
      default: return true;
    }
  };

  const cardStyle = (isSelected) => ({
    padding: '16px 20px',
    borderRadius: '12px',
    border: isSelected ? '3px solid #10B981' : '2px solid rgba(255,255,255,0.2)',
    background: isSelected ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255,255,255,0.05)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  });

  const chipStyle = (isSelected) => ({
    padding: '10px 16px',
    borderRadius: '20px',
    border: isSelected ? '2px solid #10B981' : '2px solid rgba(255,255,255,0.2)',
    background: isSelected ? 'rgba(16, 185, 129, 0.15)' : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '14px'
  });

  const inputStyle = {
    width: '100%',
    padding: '14px',
    borderRadius: '10px',
    border: '2px solid rgba(255,255,255,0.2)',
    background: 'rgba(255,255,255,0.05)',
    color: 'white',
    fontSize: '16px',
    boxSizing: 'border-box'
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      color: 'white',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', paddingTop: '40px' }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>📝</div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
            {isEditing ? 'Edit Research Profile' : 'EMIT Research Setup'}
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.7)' }}>
            Let's personalize your academic writing experience
          </p>
        </div>

        {/* Progress */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '30px' }}>
          {[1,2,3,4,5].map(s => (
            <div key={s} style={{
              width: '40px',
              height: '6px',
              borderRadius: '3px',
              background: s <= step ? '#10B981' : 'rgba(255,255,255,0.2)'
            }} />
          ))}
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.2)', padding: '12px', borderRadius: '8px', marginBottom: '20px', textAlign: 'center' }}>
            {error}
          </div>
        )}

        {/* Step 1: Academic Level */}
        {step === 1 && (
          <div>
            <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
              What is your academic level?
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {academicLevels.map(level => (
                <div
                  key={level.value}
                  onClick={() => setProfile({...profile, academic_level: level.value})}
                  style={cardStyle(profile.academic_level === level.value)}
                >
                  <div style={{ fontSize: '28px' }}>{level.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '16px' }}>{level.label}</div>
                    <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>{level.desc}</div>
                  </div>
                  {profile.academic_level === level.value && (
                    <div style={{ color: '#10B981', fontSize: '20px' }}>✓</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Institution & Field */}
        {step === 2 && (
          <div>
            <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
              Tell us about your studies
            </h2>
            
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                Institution Name
              </label>
              <input
                type="text"
                value={profile.institution}
                onChange={(e) => setProfile({...profile, institution: e.target.value})}
                placeholder="e.g., Liberty University, Duke Divinity School"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                Field of Study / Major
              </label>
              <input
                type="text"
                value={profile.field_of_study}
                onChange={(e) => setProfile({...profile, field_of_study: e.target.value})}
                placeholder="e.g., Theology, Business Administration, Nursing"
                style={inputStyle}
              />
            </div>
          </div>
        )}

        {/* Step 3: Citation Style */}
        {step === 3 && (
          <div>
            <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
              Preferred Citation Style
            </h2>
            <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', marginBottom: '20px' }}>
              You can always specify a different style for individual papers
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {citationStyles.map(style => (
                <div
                  key={style.value}
                  onClick={() => setProfile({...profile, preferred_citation_style: style.value})}
                  style={cardStyle(profile.preferred_citation_style === style.value)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '16px' }}>{style.label}</div>
                    <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>{style.desc}</div>
                  </div>
                  {profile.preferred_citation_style === style.value && (
                    <div style={{ color: '#10B981', fontSize: '20px' }}>✓</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Writing Goals */}
        {step === 4 && (
          <div>
            <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
              What are your writing goals?
            </h2>
            <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', marginBottom: '20px' }}>
              Select all that apply
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
              {writingGoals.map(goal => (
                <div
                  key={goal}
                  onClick={() => toggleGoal(goal)}
                  style={chipStyle(profile.writing_goals.includes(goal))}
                >
                  {goal}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: Additional Context */}
        {step === 5 && (
          <div>
            <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
              Anything else we should know?
            </h2>
            <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', marginBottom: '20px' }}>
              Optional: Share any additional context that will help personalize your experience
            </p>
            <textarea
              value={profile.additional_context}
              onChange={(e) => setProfile({...profile, additional_context: e.target.value})}
              placeholder="e.g., I'm working on my Master's thesis about pastoral leadership. My advisor requires strict Turabian formatting..."
              rows={5}
              style={{...inputStyle, resize: 'vertical'}}
            />
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
          {step > 1 ? (
            <button
              onClick={() => setStep(step - 1)}
              style={{
                padding: '14px 28px',
                borderRadius: '10px',
                border: '2px solid rgba(255,255,255,0.3)',
                background: 'transparent',
                color: 'white',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Back
            </button>
          ) : <div />}

          {step < 5 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              style={{
                padding: '14px 28px',
                borderRadius: '10px',
                border: 'none',
                background: canProceed() ? '#10B981' : 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '16px',
                cursor: canProceed() ? 'pointer' : 'not-allowed',
                fontWeight: '600'
              }}
            >
              Continue
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              style={{
                padding: '14px 28px',
                borderRadius: '10px',
                border: 'none',
                background: '#10B981',
                color: 'white',
                fontSize: '16px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Complete Setup')}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default EmitResearchOnboarding;
